import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type NurseWarningState, WarningDialog } from "../../utils/warningDialogs/WarningDialog";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import { MultiAddReviewDialog, type MultiAddReviewDialogProps } from "./MultiAddReviewDialog";

/** The confirm handlers `useNurseWarnings` returns after `[state, check]`, in its tuple order. */
type WarningHandlers = [
	onAgencyConfirm: () => void,
	onOvertimeConfirm: () => void,
	onDailyHoursConfirm: () => void,
	onCancel: () => void,
	onPtoConfirm: () => void,
	onTargetConfirm: () => void,
	onEmployeeStatusConfirm: () => void,
];

export type AddStaffDialogsProps = {
	warningState: NurseWarningState;
	warningHandlers: WarningHandlers;
	longShiftDuration: number | undefined;
	/** Actuals mode: "this changes actual working hours" — confirmed before the warning cascade. */
	payrollConfirmOpen: boolean;
	onPayrollCancel: () => void;
	onPayrollConfirm: () => void;
	/** Several picks: the one combined review that stands in for the single add's dialog cascade. */
	multiAddReview: MultiAddReviewDialogProps;
};

/** The add's confirmations: the payroll check (actuals), the single add's warning cascade, and the multi-add review. */
export const AddStaffDialogs: FC<AddStaffDialogsProps> = ({
	warningState,
	warningHandlers,
	longShiftDuration,
	payrollConfirmOpen,
	onPayrollCancel,
	onPayrollConfirm,
	multiAddReview,
}) => {
	const { t } = useTranslation();
	const [onAgencyConfirm, onOvertimeConfirm, onDailyHoursConfirm, onCancel, onPtoConfirm, onTargetConfirm, onEmployeeStatusConfirm] = warningHandlers;
	return (
		<>
			<WarningDialog
				openTargetWarning={warningState.isTargetWarningOpen}
				openPtoWarning={warningState.isPtoWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				openEmployeeStatusWarning={warningState.isEmployeeStatusWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				targetDetails={warningState.targetDetails}
				employeeStatusDetails={warningState.employeeStatusDetails}
				longShiftDuration={longShiftDuration}
				onCancelTarget={onCancel}
				onConfirmTarget={onTargetConfirm}
				onCancelPto={onCancel}
				onConfirmPto={onPtoConfirm}
				onCancelAgency={onCancel}
				onConfirmAgency={onAgencyConfirm}
				onCancelOvertime={onCancel}
				onConfirmOvertime={onOvertimeConfirm}
				onCancelDailyHours={onCancel}
				onConfirmDailyHours={onDailyHoursConfirm}
				onCancelEmployeeStatus={onCancel}
				onConfirmEmployeeStatus={onEmployeeStatusConfirm}
			/>
			{/* The explicit button ids keep the long-standing `add-nurse-confirm-dialog-{cancel,confirm}` the e2e suite uses. */}
			<SystemDialog
				open={payrollConfirmOpen}
				onClose={onPayrollCancel}
				title={t("confirm.add")}
				body={t("actual.hours.warning")}
				onConfirm={onPayrollConfirm}
				cancelButtonVariant="secondary"
				confirmButtonVariant="primary"
				dataTestId="add-nurse-confirm-dialog"
				cancelDataTestId="add-nurse-confirm-dialog-cancel"
				confirmDataTestId="add-nurse-confirm-dialog-confirm"
			/>
			<MultiAddReviewDialog {...multiAddReview} />
		</>
	);
};
